/**
 * Minimap.tsx — Plan 05-04 Task 2 (UI-03).
 *
 * Top-down SVG minimap rendered OUTSIDE the R3F Canvas as a DOM overlay.
 * Visible only during 'freeWalk' and 'guidedTour' navStates — hidden while
 * the user is idle, boarding, riding, in the arrival cinematic, or in the
 * photo-compare rig.
 *
 * Subscriptions (Zustand):
 *   - useNavStore.navState      — visibility gate
 *   - useNavStore.cameraPosition — player position (projection origin)
 *   - useNavStore.cameraQuaternion — yaw extraction for the player-triangle
 *
 * POI data is driven by manifest.minimapPois[] (authored in world.json).
 * Projection math lives in minimap-math.ts (pure, unit-tested).
 *
 * Chrome (AESTHETIC-BRIEF Rule 7):
 *   - Dark slate circular background #2B2A28 with a 1.5px warm-ochre #D4A87E
 *     border — per 05-RESEARCH.md Pattern 5 recommendation
 *   - POI dots in warm-ochre (#D4A87E) — a muted clay alternative
 *     (#B8602C) was considered but kept as accent only
 *   - Player triangle in cream (#F0E6D2) — matches the serif chrome family
 *   - NO glass morphism, NO modern rounded cards, NO shader-heavy FX
 *
 * Position: fixed bottom-right. Leaves EraToggle (bottom-center) and
 * SourcesPanel (right side) undisturbed.
 *
 * ANTI-PATTERN GUARD: this component is DOM-scope (outside the R3F Canvas)
 * and therefore cannot use the per-frame R3F hook. Only Zustand selectors
 * drive re-render on camera pose changes — StreetcarRig / WalkRig / TourRig
 * write `cameraPosition` + `cameraQuaternion` via useNavStore.setCameraPose
 * every frame, which triggers Minimap re-render at roughly 60 Hz. If
 * profiling surfaces this as a hotspot in Phase 7 perf work, add a
 * requestAnimationFrame-throttled selector per 05-RESEARCH.md Pitfall 6.
 */
import { useNavStore } from '../store/useNavStore';
import { useManifest } from '../content/useManifest';
import {
  MAP_SIZE_PX,
  quaternionToYaw,
  worldToLocal,
  isInView,
} from './minimap-math';

// ---- Palette ----
const DARK_SLATE = '#2B2A28'; // warm-dark background (slightly warmer than pure slate)
const WARM_OCHRE = '#D4A87E'; // border + POI dots
const CREAM = '#F0E6D2';      // player triangle

export function Minimap() {
  const navState = useNavStore((s) => s.navState);
  const cameraPosition = useNavStore((s) => s.cameraPosition);
  const cameraQuaternion = useNavStore((s) => s.cameraQuaternion);
  const { manifest } = useManifest();

  // Visibility gate — per plan must_haves.truths[5]
  if (navState !== 'freeWalk' && navState !== 'guidedTour') return null;

  // Graceful degrade — if the manifest hasn't resolved yet (first frame after
  // mount, or on error) return null rather than render an empty shell
  if (!manifest) return null;

  const size = MAP_SIZE_PX;
  const center = size / 2;

  // Extract yaw; convert to degrees. SVG rotate() takes degrees; we negate so
  // that a positive world-yaw (Y-axis CCW when looking down) rotates the
  // player triangle CCW in SVG coordinates (where +rotation is CW because
  // +Y is DOWN). The sign convention matches what users expect when steering.
  const yawRad = quaternionToYaw(cameraQuaternion);
  const yawDeg = (-yawRad * 180) / Math.PI;

  // Project each POI into local SVG coords and drop any that fall outside
  // the map window. The square bounds-check is a fast pre-filter; a circular
  // clip could be added if POIs start crowding the corners.
  const visiblePois = (manifest.minimapPois ?? [])
    .map((poi) => {
      const [wx, , wz] = poi.position;
      const [lx, ly] = worldToLocal(wx, wz, cameraPosition, size);
      return { poi, lx, ly };
    })
    .filter(({ lx, ly }) => isInView(lx, ly, size));

  return (
    <div
      role="img"
      aria-label="Minimap showing nearby points of interest"
      style={{
        position: 'fixed',
        bottom: '1rem',
        right: '1rem',
        width: size,
        height: size,
        zIndex: 30,
        pointerEvents: 'none', // minimap is purely informational for v1
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Background circle — dark warm-slate with warm-ochre border */}
        <circle
          data-background
          cx={center}
          cy={center}
          r={center - 1}
          fill={DARK_SLATE}
          stroke={WARM_OCHRE}
          strokeWidth={1.5}
        />

        {/* POI dots — warm-ochre, manifest-driven */}
        {visiblePois.map(({ poi, lx, ly }) => (
          <circle
            key={poi.id}
            data-poi-id={poi.id}
            cx={lx}
            cy={ly}
            r={3}
            fill={WARM_OCHRE}
          />
        ))}

        {/* Player triangle — centered, rotates with camera yaw */}
        <g
          data-player
          transform={`translate(${center} ${center}) rotate(${yawDeg.toFixed(2)})`}
        >
          <polygon
            points="0,-6 -4,4 4,4"
            fill={CREAM}
            stroke={DARK_SLATE}
            strokeWidth={0.5}
          />
        </g>
      </svg>
    </div>
  );
}
